import React, {useMemo, useState} from "react";
import {FaCheck, FaExclamationCircle, FaFolderOpen} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {functions} from "../../../../../../firebase";
import useUserFeedbackMessage from "../../../../../../hooks/useUserFeedbackMessage";
import {selectAccounts} from "../../../../../../redux/account/account.selectors";
import {updateAsset} from "../../../../../../redux/asset/asset.actions";
import {selectActiveAsset} from "../../../../../../redux/asset/asset.selectors";
import {renderDate} from "../../../../../../utils/RenderDate";
import ReelerButton from "../../../../../commons/ReelerButton";
import ReelerTooltip from "../../../../../commons/ReelerTooltip/ReelerTooltip";
import AssetTextField from "./AssetTextField";

const getMainVersion = (asset) => {
  if (Array.isArray(asset?.assetVersions) && asset.assetVersions.length > 0) {
    const mainVersion = asset.assetVersions.find(
      (version) => version.main === true && version.url,
    );

    if (mainVersion) {
      return mainVersion;
    }
  }

  if (asset?.url) {
    return {
      id: asset.id,
      url: asset.url,
      main: true,
    };
  }

  return null;
};

export default function AssetScaleflex() {
  const dispatch = useDispatch();
  const asset = useSelector(selectActiveAsset);
  const account = useSelector(selectAccounts);
  const {setSuccessMessage, setErrorMessage} = useUserFeedbackMessage();
  const [loading, setLoading] = useState(false);

  const accountScaleflexIntegration = account?.integrations?.scaleflex;
  const assetScaleflex = asset?.scaleflex;
  const mainVersion = useMemo(() => getMainVersion(asset), [asset]);
  const hasSuccessfulSend = assetScaleflex?.status === "success";

  if (!accountScaleflexIntegration) {
    return null;
  }

  const handleSendToScaleflex = async () => {
    if (!asset?.id) return;

    if (!mainVersion?.url) {
      setErrorMessage(
        "The asset has no main version URL to send to Scaleflex.",
      );
      return;
    }

    setLoading(true);

    try {
      const sendAssetToScaleflex = functions.httpsCallable(
        "sendAssetToScaleflex",
      );
      const {data} = await sendAssetToScaleflex({assetId: asset.id});

      dispatch(
        updateAsset({
          scaleflex: data,
          tags: {
            ...(asset?.tags || {}),
            scaleflex: true,
          },
        }),
      );

      setSuccessMessage("Asset sent to Scaleflex.");
    } catch (error) {
      console.log(error);

      if (error?.details?.scaleflex) {
        dispatch(
          updateAsset({
            scaleflex: error.details.scaleflex,
          }),
        );
      }

      setErrorMessage(
        error?.message ||
          "Could not send the asset to Scaleflex. Please try again.",
      );
    }

    setLoading(false);
  };

  return (
    <AssetTextField
      icon={
        <ReelerTooltip text="Scaleflex">
          <FaFolderOpen className="icon-color" />
        </ReelerTooltip>
      }
      text={
        <div className="d-flex flex-column">
          <div className="d-flex flex-row align-items-center flex-wrap gap-2">
            {!hasSuccessfulSend ? (
              <ReelerButton
                loading={loading}
                dispatch={handleSendToScaleflex}
                text="Send to Scaleflex"
                styleClass="btn-secondary"
              />
            ) : null}
            {assetScaleflex?.status === "success" ? (
              <div className="d-flex flex-row align-items-center">
                <FaCheck className="me-1" style={{color: "var(--reeler)"}} />
                <span>
                  Sent to Scaleflex
                  {assetScaleflex?.lastSentAt
                    ? ` (${renderDate(
                        assetScaleflex.lastSentAt,
                        "YYYY-MM-DD",
                      )})`
                    : ""}
                </span>
              </div>
            ) : null}
            {assetScaleflex?.status === "failed" ? (
              <div className="d-flex flex-row align-items-center text-danger">
                <FaExclamationCircle className="me-1" />
                <span>Failed</span>
              </div>
            ) : null}
          </div>

          <div className="mt-2 small">
            {assetScaleflex?.fileId ? (
              <div>Scaleflex id: {assetScaleflex.fileId}</div>
            ) : null}
            {assetScaleflex?.error ? (
              <div className="text-danger" style={{overflowWrap: "anywhere"}}>
                Last error: {assetScaleflex.error}
              </div>
            ) : null}
          </div>
        </div>
      }
    />
  );
}
